import { useState, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { CheckCircle, RefreshCw, ThumbsUp, ThumbsDown, Volume2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import useReducedMotion from '../../hooks/useReducedMotion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


/**
 * Extracts vocabulary data from various backend shapes.
 * @param {Object} lesson
 * @returns {Array|null}
 */
function getVocabulary(lesson) {
  const raw =
    lesson?.content?.vocabulary ||
    lesson?.vocabulary ||
    lesson?.content?.words ||
    null;

  if (raw && Array.isArray(raw) && raw.length > 0) {
    return raw.map((v, i) => ({
      id: v.id || i + 1,
      word: v.word || v.front || v.term || '',
      translation: v.translation || v.back || v.definition || '',
      example: v.example || v.example_sentence || '',
    }));
  }
  return null;
}

const SWIPE_THRESHOLD = 80; // px before card commits

/**
 * SwipeVocabularyLesson — Tinder-tarzi swipe kelime alıştırması (lesson_type === 'swipe').
 *
 * Sağ swipe = "Biliyorum", sol swipe = "Tekrar Et".
 * "Tekrar Et" kartları tekrar kuyruğa alınır.
 *
 * Backend content shape (Faz 4e ile gelecek):
 *   lesson.content.vocabulary: [{ word, translation, example, audio? }]
 *
 * @param {{ lesson: Object, courseId: string, onComplete: Function, completedLessonIds: string[] }} props
 */
export default function SwipeVocabularyLesson({ lesson, courseId, onComplete, completedLessonIds = [] }) {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const reduced = useReducedMotion();

  const lessonId = lesson?.lesson_id;
  const isCompleted = completedLessonIds.includes(lessonId);

  const rawVocab = getVocabulary(lesson);
  const hasContent = rawVocab !== null && rawVocab.length > 0;
  const initialVocab = hasContent ? rawVocab : [];
  const originalTotal = initialVocab.length;

  const [deck, setDeck] = useState(initialVocab);
  const [knownIds, setKnownIds] = useState(new Set());
  const [reviewIds, setReviewIds] = useState(new Set());
  const [finished, setFinished] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null); // 'left' | 'right' | null (for button hints)
  const [isExiting, setIsExiting] = useState(false);

  const currentCard = deck[0];

  const speakText = useCallback((text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = 'en-US';
    window.speechSynthesis.speak(utt);
  }, []);

  const commitSwipe = useCallback((direction) => {
    if (isExiting || !currentCard) return;
    setIsExiting(true);
    setSwipeDirection(direction);

    setTimeout(() => {
      setDeck(prev => {
        const [top, ...rest] = prev;
        if (direction === 'right') {
          // Known — remove from deck
          setKnownIds(k => new Set([...k, top.id]));
          if (rest.length === 0) {
            setFinished(true);
          }
          return rest;
        } else {
          // Review later — put at end
          setReviewIds(r => new Set([...r, top.id]));
          const newDeck = [...rest, top];
          // Finished only if every unique card has been "known"
          const uniqueLeft = newDeck.filter(c => !knownIds.has(c.id));
          if (uniqueLeft.length === 0) setFinished(true);
          return newDeck;
        }
      });
      setIsExiting(false);
      setSwipeDirection(null);
    }, 300);
  }, [isExiting, currentCard, knownIds]);

  const handleKnown = () => commitSwipe('right');
  const handleReview = () => commitSwipe('left');

  const restart = () => {
    setDeck(initialVocab);
    setKnownIds(new Set());
    setReviewIds(new Set());
    setFinished(false);
    setSwipeDirection(null);
  };

  const handleComplete = async () => {
    if (completing) return;
    setCompleting(true);
    const score = originalTotal > 0 ? Math.round((knownIds.size / originalTotal) * 100) : 100;
    try {
      await axios.post(
        `${API}/enrollments/complete-lesson`,
        { course_id: courseId, lesson_id: lessonId, quiz_score: score },
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? 'Ders tamamlandı!' : 'Lesson completed!');
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setCompleting(false);
    }
  };

  if (isCompleted) {
    return (
      <div className="rounded-2xl border-2 border-green-200 bg-green-50 p-6 mb-6 text-center" data-testid="swipe-already-completed">
        <CheckCircle size={32} className="mx-auto text-green-600 mb-2" />
        <p className="font-bold text-sm text-green-700">
          {lang === 'tr' ? 'Bu ders tamamlandı' : 'This lesson is completed'}
        </p>
      </div>
    );
  }

  if (!hasContent) {
    return (
      <div className="text-center py-12" data-testid="swipe-empty-state">
        <CheckCircle size={40} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500 font-semibold mb-4">
          {lang === 'tr' ? 'Bu derste henüz kelime içeriği tanımlanmamış.' : 'No vocabulary content has been defined for this lesson yet.'}
        </p>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleComplete}
          disabled={completing}
          className="px-6 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-50"
          data-testid="swipe-empty-complete-btn"
        >
          {completing ? '...' : t('common.completeLesson')}
        </motion.button>
      </div>
    );
  }

  return (
    <div data-testid="swipe-lesson">
      {/* Stats bar */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3 text-xs font-bold">
          <span className="flex items-center gap-1 text-emerald-600">
            <ThumbsUp size={14} /> {knownIds.size} {t('swipeLesson.known')}
          </span>
          <span className="flex items-center gap-1 text-amber-500">
            <ThumbsDown size={14} /> {reviewIds.size} {t('swipeLesson.reviewLater')}
          </span>
        </div>
        <span className="text-xs font-bold text-gray-400">
          {deck.length} {t('swipeLesson.remaining')}
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden border border-gray-300 mb-6">
        <motion.div
          className="h-full bg-cyan-500 rounded-full"
          animate={{ width: `${(knownIds.size / originalTotal) * 100}%` }}
          transition={reduced ? { duration: 0 } : { type: 'spring', stiffness: 80 }}
        />
      </div>

      {/* Finished screen */}
      <AnimatePresence mode="wait">
        {finished ? (
          <motion.div
            key="swipe-done"
            initial={reduced ? {} : { opacity: 0, scale: 0.92 }}
            animate={reduced ? {} : { opacity: 1, scale: 1 }}
            className="rounded-2xl border-2 border-cyan-200 bg-cyan-50 p-8 text-center"
            data-testid="swipe-completed"
          >
            <div className="w-20 h-20 rounded-full bg-cyan-500 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} className="text-white" />
            </div>
            <h2 className="font-display text-3xl font-bold text-cyan-700 mb-1">
              {t('swipeLesson.completed')}
            </h2>
            <p className="text-gray-600 font-semibold mb-6">
              {knownIds.size}/{originalTotal} {t('swipeLesson.wordsKnown')} — {t('swipeLesson.score')}: %{Math.round((knownIds.size / originalTotal) * 100)}
            </p>
            <div className="flex justify-center gap-4">
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={handleComplete}
                disabled={completing}
                className="px-8 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-50 flex items-center gap-2"
                data-testid="swipe-complete-btn"
              >
                <CheckCircle size={18} />
                {completing ? '...' : t('common.completeLesson')}
              </motion.button>
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={restart}
                className="px-6 py-3 rounded-xl border-2 border-gray-300 bg-white font-bold shadow-[0_4px_0_#d1d5db] active:shadow-none active:translate-y-1 transition-all flex items-center gap-2 text-gray-700"
                data-testid="swipe-restart-btn"
              >
                <RefreshCw size={16} /> {t('swipeLesson.restart')}
              </motion.button>
            </div>
          </motion.div>
        ) : (
          <div key="swipe-active">
            {/* Card stack */}
            <div className="relative flex items-center justify-center" style={{ minHeight: '300px' }} data-testid="swipe-card-area">
              {/* Background cards (stack effect) */}
              {deck.slice(1, 3).map((card, stackIdx) => (
                <div
                  key={card.id}
                  className="absolute w-full rounded-2xl border-2 border-gray-200 bg-white"
                  style={{
                    transform: `scale(${1 - (stackIdx + 1) * 0.04}) translateY(${(stackIdx + 1) * 8}px)`,
                    zIndex: 2 - stackIdx,
                    top: 0,
                    left: 0,
                    right: 0,
                    minHeight: '280px',
                  }}
                />
              ))}

              {/* Top card */}
              {currentCard && (
                <SwipeCard
                  key={currentCard.id}
                  card={currentCard}
                  reduced={reduced}
                  isExiting={isExiting}
                  swipeDirection={swipeDirection}
                  t={t}
                  lang={lang}
                  onSwipe={commitSwipe}
                  onSpeak={speakText}
                />
              )}
            </div>

            {/* Swipe instruction */}
            <p className="text-center text-xs text-gray-400 font-semibold mt-3 mb-4">
              {t('swipeLesson.swipeInstruction')}
            </p>

            {/* Button controls */}
            <div className="flex gap-4 justify-center mt-2">
              <motion.button
                whileHover={reduced ? {} : { scale: 1.05 }}
                whileTap={reduced ? {} : { scale: 0.95 }}
                onClick={handleReview}
                disabled={isExiting || !currentCard}
                className="flex flex-col items-center gap-1 px-8 py-4 rounded-2xl border-2 border-amber-400 bg-amber-50 text-amber-600 font-bold shadow-[0_4px_0_#f59e0b] active:shadow-none active:translate-y-1 transition-all disabled:opacity-40"
                aria-label={t('swipeLesson.reviewLater')}
                data-testid="swipe-review-btn"
              >
                <ThumbsDown size={22} />
                <span className="text-xs">{t('swipeLesson.reviewLater')}</span>
              </motion.button>

              <motion.button
                whileHover={reduced ? {} : { scale: 1.05 }}
                whileTap={reduced ? {} : { scale: 0.95 }}
                onClick={handleKnown}
                disabled={isExiting || !currentCard}
                className="flex flex-col items-center gap-1 px-8 py-4 rounded-2xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-40"
                aria-label={t('swipeLesson.iKnow')}
                data-testid="swipe-known-btn"
              >
                <ThumbsUp size={22} />
                <span className="text-xs">{t('swipeLesson.iKnow')}</span>
              </motion.button>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SwipeCard({ card, reduced, isExiting, swipeDirection, t, lang, onSwipe, onSpeak }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-150, 0, 150], [-18, 0, 18]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0, 1, 1, 1, 0]);
  const rightBgOpacity = useTransform(x, [0, 100], [0, 1]);
  const leftBgOpacity = useTransform(x, [-100, 0], [1, 0]);

  const handleDragEnd = useCallback((_, info) => {
    if (info.offset.x > SWIPE_THRESHOLD) {
      onSwipe('right');
    } else if (info.offset.x < -SWIPE_THRESHOLD) {
      onSwipe('left');
    }
  }, [onSwipe]);

  // Button-triggered exit animation
  const exitX = isExiting
    ? (swipeDirection === 'right' ? 300 : swipeDirection === 'left' ? -300 : 0)
    : 0;

  return (
    <motion.div
      drag={reduced ? false : 'x'}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.8}
      onDragEnd={handleDragEnd}
      style={{ x: reduced ? 0 : x, rotate: reduced ? 0 : rotate, opacity: reduced ? 1 : opacity, zIndex: 10 }}
      animate={isExiting ? { x: exitX, opacity: 0, transition: { duration: 0.3 } } : {}}
      className="absolute inset-0 cursor-grab active:cursor-grabbing select-none"
      data-testid="swipe-top-card"
      role="article"
      aria-label={`${card.word} — ${card.translation}`}
    >
      {/* Right-swipe indicator */}
      {!reduced && (
        <motion.div
          style={{ opacity: rightBgOpacity }}
          className="absolute top-4 left-4 px-3 py-1.5 rounded-xl border-2 border-emerald-400 bg-emerald-50 text-emerald-600 font-bold text-sm z-20 rotate-[-12deg]"
        >
          {t('swipeLesson.iKnow')} ✓
        </motion.div>
      )}
      {/* Left-swipe indicator */}
      {!reduced && (
        <motion.div
          style={{ opacity: leftBgOpacity }}
          className="absolute top-4 right-4 px-3 py-1.5 rounded-xl border-2 border-amber-400 bg-amber-50 text-amber-600 font-bold text-sm z-20 rotate-[12deg]"
        >
          {t('swipeLesson.reviewLater')}
        </motion.div>
      )}

      <div className="w-full h-full min-h-[280px] rounded-2xl border-2 border-cyan-300 bg-white shadow-[0_8px_0_#06b6d4] flex flex-col items-center justify-center p-8 text-center">
        <p className="font-display text-4xl font-bold text-gray-800 mb-4">{card.word}</p>
        <p className="text-xl font-bold text-cyan-600 mb-4">{card.translation}</p>
        {card.example && (
          <p className="text-sm text-gray-500 italic border-t border-gray-100 pt-4 mt-2 max-w-xs">
            "{card.example}"
          </p>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onSpeak(card.word); }}
          aria-label={lang === 'tr' ? 'Sesi oynat' : 'Play audio'}
          className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-cyan-200 bg-cyan-50 text-cyan-600 font-bold text-xs hover:bg-cyan-100 transition-colors"
          data-testid="swipe-speak-btn"
        >
          <Volume2 size={14} /> {lang === 'tr' ? 'Dinle' : 'Listen'}
        </button>
      </div>
    </motion.div>
  );
}
